import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { listenAvbpStaff } from '../../lib/staffRoles';
import { findOrGenerateParty } from '../lib/partyMatch';
import { generateClientId, invoicePrefixPreview } from '../lib/clientId';
import Modal from '../components/Modal';
import type {
  Client,
  ClientStatus,
  Currency,
  FirmId,
  Geo,
  Intent,
  LeadSource,
  PartyMatch,
  ServiceInterest,
} from '../types/client';

const LEAD_SOURCES: Array<{ v: LeadSource; l: string }> = [
  { v: 'referral', l: 'Referral' },
  { v: 'manual_entry', l: 'Manual entry / Walk-in' },
  { v: 'home_form', l: 'Home form' },
  { v: 'contact_page', l: 'Contact page' },
  { v: 'service_page', l: 'Service page' },
  { v: 'service_enquiry', l: 'Service enquiry' },
  { v: 'contact', l: 'Contact form' },
  { v: 'newsletter', l: 'Newsletter' },
  { v: 'guide_download', l: 'Guide download' },
  { v: 'property_score', l: 'Property score' },
  { v: 'room_assessment', l: 'Room assessment' },
  { v: 'plot_check', l: 'Plot check' },
  { v: 'exit_popup', l: 'Exit popup' },
  { v: 'E1', l: 'E1' },
  { v: 'E2', l: 'E2' },
  { v: 'E3', l: 'E3' },
  { v: 'E4', l: 'E4' },
];

const SERVICE_INTERESTS: ServiceInterest[] = ['L1A', 'L1B', 'L2A', 'L2B', 'L2C', 'L2D', 'L3A', 'L3B', 'undecided'];
const INTENTS: Intent[] = ['buying', 'living', 'building', 'not_sure'];

function ClientNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();

  const [roster, setRoster] = useState<Array<{ userId: string; name: string }>>([]);

  useEffect(() => {
    const unsub = listenAvbpStaff(
      (staffRows) => {
        setRoster(staffRows.map((s) => ({ userId: s.id, name: s.name })));
      },
      (err) => {
        console.error('Failed to listen to staff roster:', err);
      }
    );
    return unsub;
  }, []);

  // Form state
  const [firmId, setFirmId] = useState<FirmId>((params.get('firm') as FirmId) || 'firm1');
  const [name, setName] = useState(params.get('name') || '');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState(params.get('email') || '');
  const [phone, setPhone] = useState(params.get('phone') || '');
  const [whatsapp, setWhatsapp] = useState('');
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [gstin, setGstin] = useState('');
  const [company, setCompany] = useState('');
  const [geo, setGeo] = useState<Geo>('IN');
  const [currency, setCurrency] = useState<Currency>('INR');
  const [leadSource, setLeadSource] = useState<LeadSource>('referral');
  const [intent, setIntent] = useState<Intent | ''>('');
  const [serviceInterest, setServiceInterest] = useState<ServiceInterest | ''>('');
  const [decisionMaker, setDecisionMaker] = useState<'' | 'yes' | 'no' | 'unclear'>('');
  const [assignedTo, setAssignedTo] = useState<string>(user?.uid || 'unassigned');
  const [summary, setSummary] = useState('');
  const [status, setStatus] = useState<ClientStatus>('active');

  // partyId pre-seeded from cross-firm CTA, if any
  const presetPartyId = params.get('partyId') || '';

  // Modal state
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [matchModal, setMatchModal] = useState<{ partyMatch: PartyMatch; sameFirm: boolean } | null>(null);

  useEffect(() => {
    if (firmId === 'firm2') {
      setCurrency('INR');
      setGstin('');
    }
  }, [firmId]);

  // sync WA when toggling "same as phone" intent — simple convenience
  useEffect(() => {
    if (!whatsapp && phone) setWhatsapp(phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const invoicePreview = useMemo(() => invoicePrefixPreview(firmId), [firmId]);

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!name.trim() || name.trim().length < 2) e.name = 'Name required (min 2 chars).';
    if (!/^\+\d{6,15}$/.test(phone.trim())) e.phone = 'Phone must be E.164 — start with + and country code.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Valid email required.';
    if (firmId === 'firm1' && gstin && !/^[0-9A-Z]{15}$/.test(gstin.trim().toUpperCase())) {
      e.gstin = 'GSTIN must be 15 alphanumeric characters.';
    }
    if (firmId === 'firm2' && gstin) e.gstin = 'GSTIN must stay empty for HUF firm.';
    if (firmId === 'firm2' && currency !== 'INR') e.currency = 'HUF firm is INR only.';
    return e;
  };

  // Duplicate check — fired on form submit (also exposed on blur)
  const runDuplicateCheck = async (): Promise<{ partyMatch: PartyMatch; sameFirm: boolean } | null> => {
    if (!email || !phone) return null;
    const m = await findOrGenerateParty({ phone, email });
    if (!m.matched) return null;
    return { partyMatch: m, sameFirm: m.matchedFirmId === firmId };
  };

  const onBlurMatchKey = async () => {
    if (!email || !phone) return;
    const m = await findOrGenerateParty({ phone, email });
    if (m.matched) setMatchModal({ partyMatch: m, sameFirm: m.matchedFirmId === firmId });
  };

  const proceedSubmit = async (forcePartyId?: string) => {
    if (!user) return;
    setBusy(true);
    try {
      const now = new Date();
      const avbpClientId = await generateClientId(firmId, now);
      const finalPartyId = forcePartyId || presetPartyId || (await findOrGenerateParty({ phone, email })).partyId;

      const clientDoc: Omit<Client, 'createdAt' | 'updatedAt'> & {
        createdAt: ReturnType<typeof serverTimestamp>;
        updatedAt: ReturnType<typeof serverTimestamp>;
      } = {
        avbpClientId,
        partyId: finalPartyId,
        firmId,
        schemaVersion: 1,
        sourceLeadId: null,
        name: name.trim(),
        displayName: displayName.trim() || null,
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        whatsapp: whatsapp.trim() || phone.trim(),
        whatsappOptIn,
        company: company.trim() || null,
        gstin: firmId === 'firm2' ? null : (gstin.trim().toUpperCase() || null),
        billingAddress: {},
        shippingAddress: 'same',
        currency,
        leadSource,
        sourcePage: null,
        intent: (intent || null) as Intent | null,
        initialServiceInterest: (serviceInterest || null) as ServiceInterest | null,
        decisionMaker: decisionMaker === '' ? null : decisionMaker === 'yes',
        geo,
        toolScoreAtConversion: null,
        leadCreatedAt: null,
        assignedTo: assignedTo || 'unassigned',
        status,
        modules: {
          hasQuotes: false,
          hasProjects: false,
          hasPrescription: false,
          hasAppointments: false,
          hasPayments: false,
          hasDrawings: false,
          openTaskCount: 0,
        },
        brevoContactId: null,
        brevoListIds: [],
        aisensyContactId: null,
        aisensyTags: [],
        portalToken: '',
        portalTokenExpiresAt: null,
        portalLastAccessedAt: null,
        keepPortalActive: false,
        archivedAt: null,
        summary: summary.trim() || null,
        tags: [],
        createdBy: user?.uid ?? 'unknown',
        updatedBy: user?.uid ?? 'unknown',
        deletedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Atomic write — use transaction so the doc create + counter ack stay coherent
      await runTransaction(db, async (tx) => {
        const ref = doc(db, 'clients', avbpClientId);
        tx.set(ref, clientDoc);
      });

      toast('Client created.', { ok: true });
      navigate(`/clients/${avbpClientId}`, { replace: true });
    } catch (e) {
      toast((e as Error).message || 'Create failed', { ok: false });
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) {
      toast('Fix the highlighted fields.', { ok: false });
      return;
    }
    setBusy(true);
    try {
      const dup = await runDuplicateCheck();
      if (dup) {
        setMatchModal(dup);
        setBusy(false);
        return;
      }
      await proceedSubmit();
    } catch (err) {
      const msg = (err as Error).message || 'Submit failed';
      console.error('[AVBP] ClientNew.submit failed:', err);
      toast(`Could not save: ${msg}`, { ok: false });
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/clients" style={{ fontSize: 13, color: 'var(--muted)' }}>← All clients</Link>
      <h1 style={{ marginTop: 12 }}>New client</h1>
      <p className="av-help" style={{ maxWidth: 600 }}>
        Records created here are <strong>manual</strong> — leads from the website auto-promote on first paid booking.
        Use this for walk-ins, referrals, or back-fills.
      </p>

      <form onSubmit={submit} style={{ marginTop: 20, display: 'grid', gap: 16 }}>
        <div className="av-card" style={s.section}>
          <h3 style={{ marginTop: 0 }}>Identity & firm</h3>
          <div style={s.grid2}>
            <div>
              <label className="av-label">Firm</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <RadioPill checked={firmId === 'firm1'} onChange={() => setFirmId('firm1')} label="AV1 — Aayatan Veda" />
                <RadioPill checked={firmId === 'firm2'} onChange={() => setFirmId('firm2')} label="RKH — RK HUF" />
              </div>
              <div className="av-help">Locks invoice prefix and currency.</div>
            </div>
            <div>
              <label className="av-label">Invoice prefix (preview)</label>
              <input className="av-input" value={invoicePreview} readOnly style={{ fontFamily: 'var(--ff-mono)' }} />
            </div>
            <Input label="Full name" value={name} onChange={setName} error={errors.name} required />
            <Input label="Display name (optional)" value={displayName} onChange={setDisplayName} placeholder="Mr. & Mrs. Sharma" />
          </div>
        </div>

        <div className="av-card" style={s.section}>
          <h3 style={{ marginTop: 0 }}>Contact</h3>
          <div style={s.grid2}>
            <Input label="Email" value={email} onChange={(v) => setEmail(v)} onBlur={onBlurMatchKey} error={errors.email} required type="email" />
            <Input label="Phone (E.164)" value={phone} onChange={(v) => setPhone(v)} onBlur={onBlurMatchKey} error={errors.phone} placeholder="+919876543210" required />
            <Input label="WhatsApp (E.164)" value={whatsapp} onChange={setWhatsapp} placeholder="defaults to phone" />
            <div>
              <label className="av-label">WhatsApp opt-in</label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={whatsappOptIn} onChange={(e) => setWhatsappOptIn(e.target.checked)} /> Yes — they consented
              </label>
            </div>
          </div>
        </div>

        <div className="av-card" style={s.section}>
          <h3 style={{ marginTop: 0 }}>Firm & invoicing</h3>
          <div style={s.grid2}>
            <Input label="Company (optional)" value={company} onChange={setCompany} />
            <Input
              label={firmId === 'firm1' ? 'GSTIN (B2B only)' : 'GSTIN (HUF — must be empty)'}
              value={gstin}
              onChange={(v) => setGstin(v.toUpperCase())}
              disabled={firmId === 'firm2'}
              error={errors.gstin}
            />
            <Select
              label="Currency"
              value={currency}
              onChange={(v) => setCurrency(v as Currency)}
              options={[{ v: 'INR', l: 'INR' }, { v: 'USD', l: 'USD' }]}
              disabled={firmId === 'firm2'}
              error={errors.currency}
            />
            <Select
              label="Geo"
              value={geo}
              onChange={(v) => setGeo(v as Geo)}
              options={[{ v: 'IN', l: 'India' }, { v: 'INTL', l: 'International' }]}
            />
          </div>
        </div>

        <div className="av-card" style={s.section}>
          <h3 style={{ marginTop: 0 }}>Origin</h3>
          <div style={s.grid2}>
            <Select
              label="Lead source"
              value={leadSource}
              onChange={(v) => setLeadSource(v as LeadSource)}
              options={LEAD_SOURCES.map((x) => ({ v: x.v, l: x.l }))}
            />
            <Select
              label="Intent"
              value={intent}
              onChange={(v) => setIntent(v as Intent)}
              options={[{ v: '', l: '—' }, ...INTENTS.map((i) => ({ v: i, l: i }))]}
            />
            <Select
              label="Initial service interest"
              value={serviceInterest}
              onChange={(v) => setServiceInterest(v as ServiceInterest)}
              options={[{ v: '', l: '—' }, ...SERVICE_INTERESTS.map((s) => ({ v: s, l: s }))]}
            />
            <Select
              label="Decision maker"
              value={decisionMaker}
              onChange={(v) => setDecisionMaker(v as 'yes' | 'no' | 'unclear' | '')}
              options={[
                { v: '', l: '—' },
                { v: 'yes', l: 'Yes' },
                { v: 'no', l: 'No' },
                { v: 'unclear', l: 'Unclear' },
              ]}
            />
          </div>
        </div>

        <div className="av-card" style={s.section}>
          <h3 style={{ marginTop: 0 }}>Status & assignment</h3>
          <div style={s.grid2}>
            <Select
              label="Status"
              value={status}
              onChange={(v) => setStatus(v as ClientStatus)}
              options={[
                { v: 'active', l: 'Active' },
                { v: 'on_hold', l: 'On hold' },
                { v: 'completed', l: 'Completed' },
                { v: 'archived', l: 'Archived' },
              ]}
            />
            <Select
              label="Assigned to"
              value={assignedTo}
              onChange={setAssignedTo}
              options={[
                { v: 'unassigned', l: 'Unassigned' },
                ...roster.map((r) => ({ v: r.userId, l: r.name })),
              ]}
            />
            <div style={{ gridColumn: 'span 2' }}>
              <label className="av-label">Summary (max 200 chars)</label>
              <textarea className="av-textarea" maxLength={200} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="One line — e.g. Flat owner, 2BHK Bhubaneswar, health concerns" />
            </div>
          </div>
        </div>

        {presetPartyId && (
          <div className="av-card" style={{ ...s.section, background: 'var(--accent-soft)' }}>
            <strong>Linking as cross-firm sibling.</strong> partyId <code>{presetPartyId}</code> will be reused for this record.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Link to="/clients" className="av-btn av-btn--ghost">Cancel</Link>
          <button type="submit" className="av-btn av-btn--primary" disabled={busy}>
            {busy ? 'Saving…' : 'Create client'}
          </button>
        </div>
      </form>

      <Modal
        open={!!matchModal}
        title={matchModal?.sameFirm ? 'Existing client in this firm' : 'Match found in the other firm'}
        onClose={() => setMatchModal(null)}
        footer={
          <>
            <button type="button" className="av-btn av-btn--ghost" onClick={() => setMatchModal(null)} disabled={busy}>
              Keep editing
            </button>
            {matchModal?.sameFirm ? (
              <>
                <Link to={`/clients/${matchModal.partyMatch.matchedClientId}`} className="av-btn">
                  Open existing record
                </Link>
                <button
                  type="button"
                  className="av-btn av-btn--danger"
                  disabled={busy}
                  onClick={() => proceedSubmit()}
                >
                  Create anyway
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="av-btn"
                  disabled={busy}
                  onClick={() => proceedSubmit()}
                >
                  Create as new party
                </button>
                <button
                  type="button"
                  className="av-btn av-btn--accent"
                  disabled={busy}
                  onClick={() => proceedSubmit(matchModal!.partyMatch.partyId)}
                >
                  Link as same party
                </button>
              </>
            )}
          </>
        }
      >
        {matchModal?.sameFirm ? (
          <p>
            A client with this phone and email already exists in this firm:
            {' '}<strong>{matchModal.partyMatch.matchedClientId}</strong>.
            You can open the existing record, keep editing, or create a duplicate
            anyway (rare — only for legitimate distinct engagements).
          </p>
        ) : (
          <p>
            We've seen this person in the other firm: <strong>{matchModal?.partyMatch.matchedClientId}</strong>.
            Link them as the same party (recommended — the partyId is reused),
            or create as a new party (rare — only for namesake-level false positives).
          </p>
        )}
      </Modal>
    </div>
  );
}

function Input({ label, value, onChange, onBlur, error, type = 'text', placeholder, required, disabled }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="av-label">{label}{required && <span style={{ color: 'var(--bad)' }}> *</span>}</label>
      <input
        className="av-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
      />
      {error && <div className="av-error">{error}</div>}
    </div>
  );
}

function Select({ label, value, onChange, options, error, disabled }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ v: string; l: string }>;
  error?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="av-label">{label}</label>
      <select className="av-select" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      {error && <div className="av-error">{error}</div>}
    </div>
  );
}

function RadioPill({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`av-btn ${checked ? 'av-btn--primary' : ''}`}
      style={{ flex: 1 }}
    >
      {label}
    </button>
  );
}

const s: Record<string, React.CSSProperties> = {
  section: { padding: 18 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 },
};

export default ClientNew;
